'use client'

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { SheetPortal } from './SheetPortal'
import { OPTOUT_COOKIE, isOptedOutClient, setOptOut } from '@/lib/tracking/optOut'
import { readCookie } from '@/lib/marketing/signupAttribution'
import { MERCHANT_BASE } from '@/lib/site/customDomainHost'

/**
 * «Your Privacy Choices» del pie: aparece solo cuando el local tiene medición
 * de anuncios activa (sin pixel no se comparte nada). Guarda la exclusión en
 * este host y recarga, para que el pixel ya cargado deje de existir. Con
 * Global Privacy Control el navegador ya se excluyó: se dice y no hay nada
 * que tocar.
 */
export function PrivacyChoices() {
  const t = useTranslations('public.microsite.privacyChoices')
  const [open, setOpen] = useState(false)
  const [state, setState] = useState<{ optedOut: boolean; byGpc: boolean } | null>(null)

  function openSheet() {
    let optedOut = false
    let byGpc = false
    try {
      byGpc = (navigator as { globalPrivacyControl?: boolean }).globalPrivacyControl === true
      optedOut = isOptedOutClient(document, navigator as { globalPrivacyControl?: boolean })
    } catch {
      // sin cookies: se muestra como no excluido
    }
    setState({ optedOut, byGpc })
    setOpen(true)
  }

  function choose(on: boolean) {
    try {
      setOptOut(document, on, window.location.protocol === 'https:')
      const stuck = (readCookie(document.cookie, OPTOUT_COOKIE) === '1') !== on
      if (stuck) return
      window.location.reload()
    } catch {
      // el navegador bloquea cookies: no hay dónde guardar la elección
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={openSheet}
        className="hover:text-white underline-offset-2 hover:underline"
      >
        Your Privacy Choices
      </button>
      {open && state && (
        <SheetPortal>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="privacy-choices-title"
            className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center"
            onClick={() => setOpen(false)}
          >
            <div
              className="relative w-full max-w-md bg-white text-gray-900 rounded-t-3xl sm:rounded-3xl shadow-2xl px-5 pt-3 pb-5 text-left"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-center pb-3">
                <div className="w-10 h-1.5 rounded-full bg-gray-300" />
              </div>
              <h2 id="privacy-choices-title" className="text-[20px] leading-tight font-extrabold tracking-[-0.02em]">
                {t('title')}
              </h2>
              <p className="mt-3 text-[13.5px] leading-snug text-gray-600">{t('body')}</p>
              <p className="mt-3 text-[13.5px] font-semibold">
                {state.optedOut ? t('statusOut') : t('statusIn')}
              </p>
              {state.byGpc && <p className="mt-2 text-xs text-gray-500">{t('gpc')}</p>}
              {/* URL absoluta: en el dominio propio una ruta relativa no llega a la política de Redy. */}
              <a
                href={`${MERCHANT_BASE}/privacy#ad-measurement`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-block text-xs font-semibold text-gray-600 underline underline-offset-2"
              >
                {t('learnMore')}
              </a>
              <div className="mt-5 flex flex-col gap-2">
                {!state.byGpc && (
                  <button
                    type="button"
                    onClick={() => choose(!state.optedOut)}
                    className="w-full rounded-2xl bg-gray-900 text-white px-4 py-3 text-sm font-bold"
                  >
                    {state.optedOut ? t('allow') : t('optOut')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="w-full rounded-2xl px-4 py-3 text-sm font-semibold text-gray-600"
                >
                  {t('close')}
                </button>
              </div>
            </div>
          </div>
        </SheetPortal>
      )}
    </>
  )
}
